import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaClock, FaFileAlt, FaTimes } from 'react-icons/fa';

const ClaimSuccessDetails = () => {
  const [details, setDetails] = useState(null);
  const [claimType, setClaimType] = useState(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("ClaimSuccessDetails mounted, checking localStorage");
    
    // Check if we have claim details in localStorage
    const storedClaimType = localStorage.getItem('claimType');
    const enquiryDetails = localStorage.getItem('claimEnquiryDetails');
    const appointmentDetails = localStorage.getItem('claimAppointmentDetails');
    
    console.log("Stored data:", { storedClaimType, 
      hasEnquiry: !!enquiryDetails, 
      hasAppointment: !!appointmentDetails 
    });
    
    if (enquiryDetails) {
      try {
        setClaimType('enquiry');
        setDetails(JSON.parse(enquiryDetails));
        setVisible(true);
        console.log("Loaded enquiry details:", JSON.parse(enquiryDetails));
      } catch (err) {
        console.error("Error parsing enquiry details:", err);
      }
    } else if (appointmentDetails) {
      try {
        setClaimType('appointment');
        setDetails(JSON.parse(appointmentDetails));
        setVisible(true);
        console.log("Loaded appointment details:", JSON.parse(appointmentDetails));
      } catch (err) {
        console.error("Error parsing appointment details:", err);
      }
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    
    // Format the date part (day with ordinal suffix)
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Format the time part (with AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} @ ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  
  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  const handleClose = () => {
    // Clean up localStorage when closing the modal
    localStorage.removeItem('claimEnquiryDetails');
    localStorage.removeItem('claimAppointmentDetails');
    localStorage.removeItem('claimType');
    localStorage.removeItem('claimToken');
    localStorage.removeItem('claimEmail');
    
    setVisible(false);
  };

  if (!visible || !details) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl shadow-2xl w-full max-w-4xl border border-purple-700/30 relative max-h-[95vh] flex flex-col">
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-purple-800/50 hover:bg-purple-700/50 rounded-full transition-all duration-200 text-gray-300 hover:text-white z-10"
        >
          <FaTimes className="h-4 w-4" />
        </button>
        
        {/* Header Section - Compact */}
        <div className="text-center py-6 px-8">
          <div className="mx-auto w-16 h-16 flex items-center justify-center bg-gradient-to-r from-[#D80364] to-[#F0654B] rounded-full mb-4 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Client Details</h2>
          <p className="text-gray-300">Here are the full details of your {claimType === 'enquiry' ? 'enquiry' : 'appointment'}:</p>
        </div>

        {/* Main Content - Scrollable if needed */}
        <div className="flex-1 px-8 pb-6 overflow-y-auto">
          <div className="bg-purple-800/40 rounded-xl p-6 border border-purple-700/30">
            <h3 className="text-xl font-semibold text-white mb-5 flex items-center">
              <div className="w-6 h-6 bg-purple-600/30 rounded-lg flex items-center justify-center mr-3">
                <FaUser className="h-3 w-3 text-purple-300" />
              </div>
              Client Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Client Name */}
              <div className="space-y-1">
                <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                  <FaUser className="h-3 w-3 mr-2" />
                  Client Name
                </div>
                <p className="text-white font-medium">{details.user_name}</p>
              </div>
              
              {/* Client Email */}
              <div className="space-y-1">
                <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                  <FaEnvelope className="h-3 w-3 mr-2" />
                  Email Address
                </div>
                <p className="text-white font-medium break-all">{details.user_email}</p>
              </div>
              
              {/* Client Phone */}
              <div className="space-y-1">
                <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                  <FaPhone className="h-3 w-3 mr-2" />
                  Phone Number
                </div>
                <p className="text-white font-medium">{details.user_phone}</p>
              </div>
              
              {/* Service */}
              <div className="space-y-1">
                <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                  <FaFileAlt className="h-3 w-3 mr-2" />
                  Legal Service
                </div>
                <p className="text-white font-medium">{details.legal_area || details.service}</p>
              </div>
              
              {/* Appointment specific details */}
              {claimType === 'appointment' && (
                <>
                  {/* Date */}
                  <div className="space-y-1">
                    <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                      <FaCalendarAlt className="h-3 w-3 mr-2" />
                      Appointment Date
                    </div>
                    <p className="text-white font-medium">
                      {details.date ? formatDate(details.date).split('@')[0].trim() : 'N/A'}
                    </p>
                  </div>
                  
                  {/* Time */}
                  <div className="space-y-1">
                    <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                      <FaClock className="h-3 w-3 mr-2" />
                      Appointment Time
                    </div>
                    <p className="text-white font-medium">{details.time || 'N/A'}</p>
                  </div>
                </>
              )}
              
              {/* Received Date */}
              <div className="space-y-1">
                <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                  <FaCalendarAlt className="h-3 w-3 mr-2" />
                  Received On
                </div>
                <p className="text-white font-medium">{formatDate(details.created_at)}</p>
              </div>
            </div>
            
            {/* Description/Notes - Full width */}
            {(details.description || details.notes) && (
              <div className="mt-6 pt-4 border-t border-purple-700/30">
                <div className="space-y-2">
                  <div className="flex items-center text-purple-300 text-xs font-medium uppercase tracking-wide">
                    <FaFileAlt className="h-3 w-3 mr-2" />
                    {claimType === 'enquiry' ? 'Enquiry Details' : 'Additional Notes'}
                  </div>
                  <div className="bg-purple-700/30 rounded-lg p-4">
                    <p className="text-white leading-relaxed text-sm">
                      {details.description || details.notes || 'No additional details provided.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Action Buttons - Fixed at bottom */}
        <div className="px-8 pb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {details.user_email && (
              <a
                href={`mailto:${details.user_email}?subject=RE: ${claimType === 'enquiry' ? 'Your Enquiry' : 'Your Appointment'}&body=Dear ${details.user_name},%0A%0AThank you for your ${claimType === 'enquiry' ? 'enquiry' : 'appointment booking'}. I'd be happy to assist you.%0A%0ABest regards,`}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:from-[#B8035A] hover:to-[#E55A43] text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-center flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                <FaEnvelope className="mr-3 text-lg" />
                Reply to Client
              </a>
            )}
            {details.user_phone && (
              <a
                href={`tel:${details.user_phone}`}
                className="flex-1 py-3 px-6 bg-white text-gray-800 hover:bg-gray-100 font-semibold rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-center flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                <FaPhone className="mr-3 text-lg text-green-600" />
                Call Client
              </a>
            )}
            <button
              onClick={handleClose}
              className="py-3 px-6 bg-purple-800/50 hover:bg-purple-700/60 border border-purple-600/50 hover:border-purple-500/70 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center shadow-lg hover:shadow-xl min-w-[140px]"
            >
              <FaTimes className="mr-3 text-lg" />
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimSuccessDetails; 